import { LANDING_REDIRECTS_QUERY } from "apollo/queries/strings/landingRedirects"
import { getLastPath } from "./getLastUrlPath"
import { graphqlFetch, type GraphqlFetchResult } from "./graphqlFetch"
import { peekCompiledRedirects } from "./redirectsCache"
import {
  emptyCompiledRedirects,
  normalizePath,
  type CompiledRedirects,
  type SubsidiaryKeyInput,
} from "./redirectTypes"

/**
 * Project-to-landing-page redirects (ASH-1887).
 *
 * For app S, a redirect `/projects/{projectSlug}` -> `/{landingSlug}` exists when
 * all three hold, evaluated independently per culture:
 *
 *   1. `project.groupSIte` contains S
 *   2. `landing.groupSite` contains S
 *   3. `landing.redirectFromProjects` contains that project
 *
 * Projects and landing pages both live centrally under
 * `/{culture}/shared/general-information/`, outside every app root, so the
 * existing `oldUrls` scan cannot see them and a single `oldUrls` value would
 * apply identically to all 25 sites.
 */

const GRAPHQL_URL = process.env.GRAPHQL_URL

const TTL_MS = 60 * 60 * 1000
const FETCH_TIMEOUT_MS = 5000

/**
 * How long to wait before retrying after a failed refresh.
 *
 * This is what keeps a CMS outage, or an environment where
 * `redirectFromProjects` has not been applied yet, from costing anything per
 * request. Without it a failed refresh leaves the map null, so every subsequent
 * request awaits its own pair of queries.
 */
const FAILURE_BACKOFF_MS = 60 * 1000

/** Server cap is 1000 items per page (`HC0051`, `maxAllowedItems: 1000`). */
const PAGE_SIZE_CAP = 1000
const MAX_PAGES = 5

/**
 * Campaign landing pages are usually temporary and a 301 is cached by browsers
 * effectively forever, so reverting to the project page later would not work for
 * anyone who visited during the campaign.
 */
const LANDING_REDIRECTS_ARE_PERMANENT = false

type CultureConfig = {
  culture: string
  route: string
  /** Locale prefix on the public site. Hebrew is the default locale and unprefixed. */
  prefix: string
  /**
   * Extra source prefixes that reach the same page. `localePrefix: "as-needed"`
   * means `/he/projects/x` is a live URL that next-intl 307s to `/projects/x`, and
   * the header's language switcher builds exactly that form
   * (`pathname.replace("/en", "/he")`). Registering it here turns a 307 then 302
   * into a single 302 when a user switches from English to Hebrew on a redirected
   * project.
   */
  aliasPrefixes: string[]
}

const CULTURES: CultureConfig[] = [
  {
    culture: "he-il",
    route: "/he-il/shared/general-information",
    prefix: "",
    aliasPrefixes: ["/he"],
  },
  {
    culture: "en-us",
    route: "/en-us/shared/general-information",
    prefix: "/en",
    aliasPrefixes: [],
  },
]

type PickerItem = { key?: string | null }
type Picker = { contentList?: PickerItem[] | null } | null

type LandingRedirectsResponse = {
  nodes?: {
    pageInfo?: { hasNextPage?: boolean; endCursor?: string | null }
    edges?: Array<{
      node?: {
        url?: string | null
        key?: string | null
        contentType?: { alias?: string | null } | null
        namedProperties?: {
          groupSIte?: Picker
          groupSite?: Picker
          redirectFromProjects?: Picker
        } | null
      }
    }>
  }
}

type LandingCacheState = {
  lastFetch: number
  compiled: CompiledRedirects | null
  /** `${culture}|${projectSlug}` -> destination, for the sitemap and the lobby (ASH-1941). */
  bySlug: Map<string, string> | null
  inFlight: Promise<void> | null
}

type GlobalWithCache = typeof globalThis & {
  __landingRedirectsCache?: Map<string, LandingCacheState>
}

const globalWithCache = global as GlobalWithCache
const caches: Map<string, LandingCacheState> = (globalWithCache.__landingRedirectsCache ??=
  new Map())

/** `ashtrom-engineering` carries four GUIDs, every other app carries one. */
export function normalizeSubsidiaryKeys(subsidiaryKey: SubsidiaryKeyInput): string[] {
  if (!subsidiaryKey) return []
  const keys = Array.isArray(subsidiaryKey) ? subsidiaryKey : [subsidiaryKey]
  return keys.filter(Boolean).map((key) => key.toLowerCase())
}

const cacheKeyFor = (appKeys: string[]): string => [...appKeys].sort().join(",")

function stateFor(appKeys: string[]): LandingCacheState {
  const key = cacheKeyFor(appKeys)
  let state = caches.get(key)
  if (!state) {
    state = { lastFetch: 0, compiled: null, bySlug: null, inFlight: null }
    caches.set(key, state)
  }
  return state
}

const pickerKeys = (picker: Picker): Set<string> =>
  new Set(
    (picker?.contentList ?? [])
      .map((item) => item?.key?.toLowerCase())
      .filter((key): key is string => Boolean(key)),
  )

export type ProjectNode = { slug: string; sites: Set<string> }
export type LandingNode = { slug: string; url: string; sites: Set<string>; from: Set<string> }

export type CultureNodes = {
  projects: Map<string, ProjectNode>
  landings: Map<string, LandingNode>
}

export type CultureFetch = {
  config: CultureConfig
  nodes: CultureNodes | null
  permanentFailure?: boolean
}

/**
 * Follows `pageInfo.hasNextPage`. A silently truncated response means silently
 * missing redirects, so a still-truncated result after `MAX_PAGES` returns null
 * and the caller keeps the previous map.
 */
type CultureNodesResult = {
  nodes: CultureNodes | null
  /** A 4xx means the query is wrong for this schema, so a quick retry cannot help. */
  permanentFailure: boolean
}

async function fetchCultureNodes({ culture, route }: CultureConfig): Promise<CultureNodesResult> {
  const projects = new Map<string, ProjectNode>()
  const landings = new Map<string, LandingNode>()

  let after: string | null = null

  // FETCH_TIMEOUT_MS is the budget for the whole culture, not for each page.
  // Pagination can issue MAX_PAGES requests, and a per-request cap would let one
  // culture block for MAX_PAGES times the cap while middleware awaits it on the
  // first request after boot.
  const deadline = Date.now() + FETCH_TIMEOUT_MS

  for (let page = 0; page < MAX_PAGES; page++) {
    const remainingMs = deadline - Date.now()
    if (remainingMs <= 0) {
      console.error(`[landing-redirects] ${culture} exhausted its ${FETCH_TIMEOUT_MS}ms budget`)
      return { nodes: null, permanentFailure: false }
    }

    // Annotated rather than inferred: `after` is both an input to this call and
    // derived from its result, which makes TypeScript give up on inference.
    const result: GraphqlFetchResult<LandingRedirectsResponse> =
      await graphqlFetch<LandingRedirectsResponse>({
        url: GRAPHQL_URL as string,
        query: LANDING_REDIRECTS_QUERY,
        operationName: "GetLandingRedirects",
        variables: { route, culture, after },
        timeoutMs: remainingMs,
      })

    const { data, error, permanentFailure } = result

    if (error || !data?.nodes) {
      console.error(`[landing-redirects] ${culture} fetch failed:`, error?.message)
      return { nodes: null, permanentFailure: Boolean(permanentFailure) }
    }

    data.nodes.edges?.forEach((edge) => {
      const node = edge?.node
      if (!node?.key || !node.url) return

      const key = node.key.toLowerCase()
      const alias = node.contentType?.alias
      const properties = node.namedProperties ?? {}

      if (alias === "project") {
        const slug = getLastPath(node.url)
        if (slug) projects.set(key, { slug, sites: pickerKeys(properties.groupSIte ?? null) })
        return
      }

      if (alias === "marketingLandingPage") {
        const slug = getLastPath(node.url)
        if (!slug) return
        landings.set(key, {
          slug,
          url: node.url,
          sites: pickerKeys(properties.groupSite ?? null),
          from: pickerKeys(properties.redirectFromProjects ?? null),
        })
      }
    })

    if (!data.nodes.pageInfo?.hasNextPage) {
      return { nodes: { projects, landings }, permanentFailure: false }
    }

    after = data.nodes.pageInfo.endCursor ?? null
    if (!after) {
      console.error(`[landing-redirects] ${culture} reports hasNextPage with no endCursor`)
      return { nodes: null, permanentFailure: false }
    }
  }

  console.error(
    `[landing-redirects] ${culture} still truncated after ${MAX_PAGES} pages of ${PAGE_SIZE_CAP}. ` +
      "Refusing to build a partial map.",
  )
  return { nodes: null, permanentFailure: false }
}

export type BuildResult = {
  compiled: CompiledRedirects
  /** `${culture}|${projectSlug}` -> destination. */
  bySlug: Map<string, string>
}

/**
 * Both cultures in one pass. Exported so `scripts/validate-landing-redirects.ts`
 * can fetch once and evaluate every app against the same response, rather than
 * reimplementing the query.
 */
export async function fetchLandingRedirectNodes(): Promise<CultureFetch[]> {
  return Promise.all(
    CULTURES.map(async (config) => {
      const { nodes, permanentFailure } = await fetchCultureNodes(config)
      return { config, nodes, permanentFailure }
    }),
  )
}

/**
 * Builds the redirect map for one app from an already-fetched response. Exported
 * for the same reason, so the validation script exercises the production rule
 * rather than a copy of it.
 */
export function buildLandingRedirectMap(
  perCulture: CultureFetch[],
  appKeys: string[],
): BuildResult {
  const result: BuildResult = {
    compiled: emptyCompiledRedirects(),
    bySlug: new Map(),
  }

  perCulture.forEach(({ config, nodes }) => {
    if (nodes) buildForCulture(config, nodes, appKeys, result)
  })

  return result
}

function buildForCulture(
  { culture, prefix, aliasPrefixes }: CultureConfig,
  { projects, landings }: CultureNodes,
  appKeys: string[],
  result: BuildResult,
): void {
  // Which landing already claimed a given source on this site, so a second claim
  // drops the entry instead of resolving by whichever node GraphQL returned first.
  const claimedBy = new Map<string, string>()
  const poisoned = new Set<string>()

  landings.forEach((landing, landingKey) => {
    landing.from.forEach((projectKey) => {
      const project = projects.get(projectKey)

      if (!project) {
        console.error(
          `[landing-redirects] ${culture}: landing "${landing.slug}" references project ` +
            `${projectKey}, which is not published in this culture. Skipping.`,
        )
        return
      }

      const applies = appKeys.some((key) => landing.sites.has(key) && project.sites.has(key))
      if (!applies) return

      const source = normalizePath(`${prefix}/projects/${project.slug}`)
      const destination = normalizePath(`${prefix}/${landing.slug}`)

      if (source === destination) {
        console.error(`[landing-redirects] ${culture}: "${source}" redirects to itself. Skipping.`)
        return
      }

      if (poisoned.has(source)) return

      // Every URL form that reaches this project page, so the redirect fires on
      // the first hop regardless of which one the user arrived on.
      const sources = [
        source,
        ...aliasPrefixes.map((alias) => normalizePath(`${alias}/projects/${project.slug}`)),
      ]

      const previousClaim = claimedBy.get(source)
      if (previousClaim && previousClaim !== landingKey) {
        console.error(
          `[landing-redirects] ${culture}: "${source}" is claimed by two landing pages ` +
            `(${previousClaim} and ${landingKey}). Dropping the redirect rather than guessing.`,
        )
        sources.forEach((value) => result.compiled.exact.delete(value))
        result.bySlug.delete(`${culture}|${project.slug}`)
        poisoned.add(source)
        return
      }

      claimedBy.set(source, landingKey)
      sources.forEach((value) => {
        result.compiled.exact.set(value, {
          destination,
          permanent: LANDING_REDIRECTS_ARE_PERMANENT,
        })
      })
      result.bySlug.set(`${culture}|${project.slug}`, destination)
    })
  })
}

/** Chains and cross-system collisions are reported, never resolved transitively. */
function reportConflicts(compiled: CompiledRedirects): void {
  const destinations = new Set(Array.from(compiled.exact.values(), (entry) => entry.destination))

  compiled.exact.forEach((entry, source) => {
    if (destinations.has(source)) {
      console.error(
        `[landing-redirects] "${source}" is both a redirect source and another rule's ` +
          "destination. Not resolving the chain.",
      )
    }
  })

  // Best effort: the oldUrls map may not have loaded yet on the first request.
  const oldUrls = peekCompiledRedirects()
  if (!oldUrls) return

  compiled.exact.forEach((_entry, source) => {
    if (oldUrls.exact.has(source)) {
      console.error(
        `[landing-redirects] "${source}" is also an oldUrls redirect source. ` +
          "The landing rule wins.",
      )
    }
  })
}

/**
 * A transient failure retries after a short window. A permanent one waits the full
 * TTL, otherwise an environment where `redirectFromProjects` has not been applied
 * logs and queries forever at many times the success-path rate.
 */
function applyFailureBackoff(state: LandingCacheState, permanent: boolean): void {
  state.lastFetch = permanent ? Date.now() : Date.now() - TTL_MS + FAILURE_BACKOFF_MS
}

/**
 * Cache empty maps rather than leaving them null. A null map makes every request
 * block on its own refresh; empty means no redirects, which is the correct degraded
 * behaviour and costs nothing.
 */
function degradeToEmptyMap(state: LandingCacheState): void {
  state.compiled = emptyCompiledRedirects()
  state.bySlug = new Map()
}

async function refresh(appKeys: string[], state: LandingCacheState): Promise<void> {
  const perCulture = await fetchLandingRedirectNodes()

  if (perCulture.some(({ nodes }) => nodes === null)) {
    applyFailureBackoff(
      state,
      perCulture.some(({ permanentFailure }) => permanentFailure),
    )

    if (state.compiled) {
      console.error("[landing-redirects] refresh incomplete, keeping the previous map")
      return
    }

    // Cache an empty map rather than leaving it null. A null map makes every
    // request block on its own refresh; an empty map means no redirects, which is
    // the correct degraded behaviour and costs nothing.
    console.error(
      "[landing-redirects] first refresh incomplete, serving an empty map. " +
        "If the error above mentions redirectFromProjects, the CMS field has not been " +
        "applied in this environment yet.",
    )
    degradeToEmptyMap(state)
    return
  }

  const result = buildLandingRedirectMap(perCulture, appKeys)

  reportConflicts(result.compiled)

  state.compiled = result.compiled
  state.bySlug = result.bySlug
  state.lastFetch = Date.now()
}

function startRefresh(appKeys: string[], state: LandingCacheState): Promise<void> {
  state.inFlight ??= refresh(appKeys, state)
    .catch((error) => {
      // Without this the map stays null after a throw, so every subsequent request
      // runs its own pair of queries with no backoff at all.
      console.error("[landing-redirects] refresh threw:", error)
      applyFailureBackoff(state, false)
      if (!state.compiled) degradeToEmptyMap(state)
    })
    .finally(() => {
      state.inFlight = null
    })

  return state.inFlight
}

/**
 * Compiled project-to-landing redirects for one app. Stale while revalidate, so
 * only the first request after process boot can wait, capped at
 * `FETCH_TIMEOUT_MS` and falling back to an empty map.
 *
 * Apps with no `/projects` route pass no `subsidiaryKey` and skip the fetch
 * entirely.
 *
 * Note on rollout: `redirectFromProjects` exists on the test CMS only. Until it
 * is applied to preprod and production, the query fails schema validation there,
 * the error is logged once per TTL, and the map stays empty. No redirects, no
 * request-path impact.
 */
export async function getLandingRedirects(
  subsidiaryKey: SubsidiaryKeyInput,
): Promise<CompiledRedirects> {
  const appKeys = normalizeSubsidiaryKeys(subsidiaryKey)
  if (!appKeys.length || !GRAPHQL_URL) return emptyCompiledRedirects()

  const state = stateFor(appKeys)

  if (!state.compiled) {
    await startRefresh(appKeys, state)
    return state.compiled ?? emptyCompiledRedirects()
  }

  if (Date.now() - state.lastFetch > TTL_MS) {
    void startRefresh(appKeys, state)
  }

  return state.compiled
}

/**
 * Project slug -> landing destination, for every project that redirects on this
 * site in this locale (ASH-1941).
 *
 * Awaits the map rather than peeking at it. Middleware runs in its own runtime
 * and does not share this module's cache with the Node server, so a route handler
 * or a server component that only peeked would always see an empty map.
 *
 * Returns an empty map when the CMS is unreachable, so callers degrade to "no
 * redirects" and keep listing every project, rather than emitting an empty
 * sitemap.
 */
export async function getRedirectedProjectSlugs(
  subsidiaryKey: SubsidiaryKeyInput,
  locale: string = "he",
): Promise<Map<string, string>> {
  const appKeys = normalizeSubsidiaryKeys(subsidiaryKey)
  if (!appKeys.length || !GRAPHQL_URL) return new Map()

  await getLandingRedirects(subsidiaryKey)

  const prefix = `${locale === "en" ? "en-us" : "he-il"}|`
  const bySlug = caches.get(cacheKeyFor(appKeys))?.bySlug
  const slugs = new Map<string, string>()

  bySlug?.forEach((destination, key) => {
    if (key.startsWith(prefix)) slugs.set(key.slice(prefix.length), destination)
  })

  return slugs
}
